import { z } from "zod";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import {
  Box,
  ChartLegend,
  DonutChart,
  Text,
  WidgetEmpty,
  WidgetInteractive,
  WidgetSkeleton,
  useElementSize,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { groupBySchema } from "#app/widgets/dashboards/use-dimension-counts";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import {
  useShapedSlices,
  sliceDrilldownIds,
  MISSING_FIELD_DESCRIPTION,
  MISSING_FIELD_TITLE,
  sliceDataFields,
} from "#app/widgets/dashboards/use-dimension-slices";
import {
  calculateSchema,
  measureSchema,
  withLegacyDataKeys,
} from "#app/widgets/dashboards/widget-measure";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { workloadTitle } from "../shared/derived-titles";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

/**
 * `type-mix` — a themed pie/donut of projects grouped by any dimension, on the
 * `DonutChart` finished chart. The composition stays calm: the mark carries
 * shape, the donut center carries the total, and a value-bearing `ChartLegend`
 * carries exact counts and shares instead of printing percentages across the
 * slices. The Display / Data / Filters editor uses v3-style icon rows (toggles
 * + compact selects) over the shared dimension vocabulary, plus the
 * `issueFilters` blob every part of the data layer folds. Top-N slicing folds
 * the tail into an "Other" wedge. Wedges size by the count per group, or by a
 * field aggregate under a Measure (`calculate`, `widget-measure.ts`); a wedge
 * is a share of a whole, so an average or an extreme prints no percentages.
 */
export const typeMixConfigSchema = withLegacyDataKeys(
  z.looseObject({
    donut: z.boolean().default(true),
    percentages: z.boolean().default(true),
    legend: z.boolean().default(true),
    measure: measureSchema,
    calculate: calculateSchema,
    groupBy: groupBySchema.default("type"),
    show: z.enum(["top5", "top8", "top12", "all"]).default("top8"),
    sort: z.enum(["volume", "alpha"]).default("volume"),
    filters: widgetFiltersSchema.nullish(),
    // Geometry stays configurable data (render reads it) without settings rows —
    // the editor keeps to the Display / Data / Filters sections. `padAngle` is
    // stored in radians; the render converts for the chart's degree surface.
    innerRatio: z.number().min(0.3).max(0.8).default(0.68),
    padAngle: z.number().min(0).max(0.06).default(0.02),
    cornerRadius: z.number().min(0).max(8).default(2),
  }),
);

export type TypeMixConfig = z.infer<typeof typeMixConfigSchema>;

const EXPANDED_CHART_MAX = 640;
const EXPANDED_LEGEND_ITEM_WIDTH = 190;
const EXPANDED_LEGEND_ROW_HEIGHT = 24;
const EXPANDED_CHART_LEGEND_GAP = 32;

function expandedChartSize(
  width: number,
  height: number,
  sliceCount: number,
  showLegend: boolean,
): number {
  if (width <= 0 || height <= 0) return 320;
  const legendColumns = Math.max(1, Math.floor(width / EXPANDED_LEGEND_ITEM_WIDTH));
  const legendRows = showLegend ? Math.ceil(sliceCount / legendColumns) : 0;
  const legendHeight = legendRows * EXPANDED_LEGEND_ROW_HEIGHT;
  const availableHeight = height - legendHeight - (showLegend ? EXPANDED_CHART_LEGEND_GAP : 0);
  return Math.max(96, Math.min(EXPANDED_CHART_MAX, width, availableHeight));
}

function TypeMixRender({ w, h, expanded, config }: WidgetRenderContext<TypeMixConfig>) {
  const { data, slices, sliceColors, dimension, measure, pending, loading, error } = useShapedSlices({
    groupBy: config.groupBy,
    measure: config.measure,
    calculate: config.calculate,
    sort: config.sort,
    show: config.show,
    filters: config.filters,
  });
  const { ref: containerRef, size: containerSize } = useElementSize<HTMLDivElement>();
  // Click-through: hosted on a dashboard (drilldown context) inside a placed
  // card (instance context), a slice press opens the Data drawer on its rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="donut" />;
  }
  if (slices.length === 0) {
    return pending ? (
      <WidgetEmpty title={MISSING_FIELD_TITLE} description={MISSING_FIELD_DESCRIPTION} />
    ) : (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const total = slices.reduce((sum, d) => sum + d.total, 0);
  const additive = !measure.measure || measure.measure.fn === "sum";
  const percentages = config.percentages && additive;
  const share = (value: number) => formatShare(value, total);
  const isDonut = config.donut;
  // Medium cards spend their body on the composition mark. A legend becomes
  // useful only once the live span can keep it below a still-large chart.
  const largeCard = !expanded && w >= 8 && h >= 5;
  const showLegend = config.legend && (expanded || largeCard);
  const expandedSize = expanded
    ? expandedChartSize(containerSize.width, containerSize.height, slices.length, showLegend)
    : undefined;

  const donutSlices = slices.map((s) => ({ id: s.id, label: s.label, value: s.total }));

  // Press → publish the slice as data (dimension + covered category ids); the
  // Dashboard host opens this instance's Data drawer filtered to it. "Other"
  // covers every folded tail id.
  const handleSliceClick =
    drill && instance && dimension
      ? (_: unknown, index: number) => {
          const s = slices[index];
          if (!s) return;
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension,
            ids: sliceDrilldownIds(s, data, slices),
            label: s.label,
          });
        }
      : undefined;

  const chart = (
    <WidgetInteractive
      fill={expanded ? undefined : true}
      position="relative"
      width={expandedSize}
      height={expandedSize}
      maxWidth={expanded ? undefined : largeCard ? 420 : 360}
      alignSelf="center"
      shrink={expanded ? false : undefined}
      transition={expanded ? "size" : undefined}
      duration={expanded ? 100 : undefined}
      cursor={handleSliceClick ? "pointer" : undefined}
    >
      <DonutChart
        data={donutSlices}
        colorTokens={sliceColors.tokens}
        innerRatio={isDonut ? config.innerRatio : 0}
        padAngle={config.padAngle * (180 / Math.PI)}
        cornerRadius={config.cornerRadius}
        onSliceClick={handleSliceClick}
        ariaLabel={measure.measure ? `${measure.label} mix` : "Project mix"}
        // The center total is the whole the wedges share. Averages and extremes
        // have no whole, so the center stays empty under those measures.
        center={
          isDonut && additive ? (
            <>
              <Text size={expanded ? "4xl" : "2xl"} weight="bold" tabular truncate={1}>
                {measure.compact(total)}
              </Text>
              <Text size={expanded ? "sm" : "xs"} variant="muted" truncate={1}>
                {measure.caption}
              </Text>
            </>
          ) : undefined
        }
        renderTooltip={(slice) => {
          const count = slices.find((s) => s.id === slice.id)?.count ?? slice.value;
          return (
            <WidgetChartTooltip
              label={slice.label}
              details={`${measure.detail(slice.value, count)}${additive ? ` · ${share(slice.value)}` : ""}`}
            />
          );
        }}
      />
    </WidgetInteractive>
  );

  const legendContent = showLegend ? (
    <ChartLegend
      items={slices.map((s, i) => ({
        label: s.label,
        colorToken: sliceColors.tokens[i],
        value: percentages
          ? `${measure.compact(s.total)} · ${share(s.total)}`
          : measure.compact(s.total),
      }))}
      direction="row"
      justify="center"
    />
  ) : null;
  const legend = expanded && legendContent ? (
    <Box fill="width" maxWidth={900}>
      {legendContent}
    </Box>
  ) : (
    legendContent
  );

  return (
    <Box
      ref={expanded ? containerRef : undefined}
      direction="column"
      fill="flex"
      gap={expanded ? "xl" : "sm"}
      align={expanded ? "center" : undefined}
      justify={expanded ? "center" : undefined}
    >
      {chart}
      {legend}
    </Box>
  );
}

/** The Display / Data / Filters editor — v3-style icon rows throughout. */
const typeMixSettings = (config: TypeMixConfig): WidgetSettingsGroup[] => [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: sliceDataFields(config),
  },
  {
    title: "Display",
    fields: [
      { key: "donut", kind: "toggle", label: "Display as a donut", icon: "donut" },
      { key: "percentages", kind: "toggle", label: "Display as percentages", icon: "percent" },
      { key: "legend", kind: "toggle", label: "Show legend", icon: "list" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const typeMixDef: WidgetModule<TypeMixConfig> = {
  type: "type-mix",
  title: "Project mix",
  dynamicTitle: (config, context) =>
    workloadTitle(config, config.groupBy, config.donut ? "Donut chart" : "Pie chart", context),
  icon: "shapes",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 4 },
  configSchema: typeMixConfigSchema,
  settings: typeMixSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "type-mix.pie",
      label: "Pie chart",
      description: "Show the composition of Projects, or of a field total, by type, stage, team, assignee, follow up, or a custom field.",
      section: "custom" satisfies WidgetSection,
      // Lands named by its gallery label. The label is not the family's while
      // every choice is built in, so it sticks like a rename until the user
      // renames the card or brings a custom field in — then the title derives
      // from the field ("Deductible by Bid Type").
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { donut: false, percentages: true, legend: true },
      defaultW: 4,
    },
    {
      key: "type-mix.donut",
      label: "Donut chart",
      description: "Show a share of Projects, or of a field total, with a center total.",
      section: "custom" satisfies WidgetSection,
      // Lands named by its gallery label. The label is not the family's while
      // every choice is built in, so it sticks like a rename until the user
      // renames the card or brings a custom field in — then the title derives
      // from the field ("Deductible by Bid Type").
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { donut: true, percentages: true, legend: true },
      defaultW: 4,
    },
    {
      key: "type-mix.outcome",
      label: "Outcome mix",
      description: "The share of Project Updates recorded under each outcome, as a pie or donut.",
      section: "outcomes" satisfies WidgetSection,
      tags: ["activity", "outcomes"] satisfies readonly WidgetTag[],
      icon: PROPERTY_ICON.followUp,
      defaultConfig: { groupBy: "outcome", donut: true, percentages: true, legend: true },
      locks: ["groupBy"],
      defaultW: 4,
    },
  ],
  render: TypeMixRender,
};
